/**
 * V3 智能拆书页面
 * 
 * 流程：上传小说 → 分析提取规则 → 生成大纲
 * 使用 MiniMax M2.5 模型
 */

import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload as UploadIcon,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  FileText,
  BookOpen,
  Wand2,
  RefreshCw,
} from 'lucide-react';
import { v3UploadBook, v3AnalyzeBook, v3GenerateOutline } from '../lib/api';

// 流程阶段
type Stage = 'upload' | 'analyzing' | 'rules' | 'generating' | 'result';

interface BookInfo {
  bookId: string;
  title: string;
  chapters: Array<{
    index: number;
    title: string;
    wordCount: number;
  }>;
  metadata: {
    totalChapters: number;
    totalWords: number;
    encoding: string;
  };
}

interface RulesInfo {
  ruleSetId: string;
  status: string;
  rules: any;
}

interface GeneratedResult {
  outlineId: string;
  outline: {
    title: string;
    genre: string;
    synopsis: string;
    chapters: Array<{
      index: number;
      title: string;
      summary: string;
      wordCount: number;
      keyPoints: string[];
    }>;
  };
  goldenChapters: {
    chapter1: string;
    chapter2: string;
    chapter3: string;
  };
}

export default function V3Page() {
  useNavigate();
  
  const [stage, setStage] = useState<Stage>('upload');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');
  
  // 数据状态
  const [bookInfo, setBookInfo] = useState<BookInfo | null>(null);
  const [rulesInfo, setRulesInfo] = useState<RulesInfo | null>(null);
  const [generatedResult, setGeneratedResult] = useState<GeneratedResult | null>(null);
  
  // 生成参数
  const [genre, setGenre] = useState('都市');
  const [desiredWords, setDesiredWords] = useState(500000);
  const [style, setStyle] = useState('');
  const [customRequirements, setCustomRequirements] = useState('');

  // 文件上传相关
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [encoding, setEncoding] = useState<'utf-8' | 'gbk' | 'auto'>('auto');

  // 处理拖拽
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // 处理文件拖放
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  // 处理文件选择
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // 阶段 1: 上传书籍
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('请先选择要上传的小说文件');
      return;
    }

    setError(null);
    setStage('analyzing');
    setProgress('正在上传小说文件...');

    try {
      const result = await v3UploadBook(selectedFile);
      setBookInfo(result);
      setProgress('文件解析完成，正在准备分析...');
      
      // 自动进入下一阶段：分析
      setTimeout(() => {
        handleAnalyze(result.bookId);
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
      setStage('upload');
    }
  };

  // 阶段 2: 分析书籍提取规则
  const handleAnalyze = async (bookId?: string) => {
    const targetBookId = bookId || bookInfo?.bookId;
    if (!targetBookId) {
      setError('缺少书籍 ID');
      setStage('upload');
      return;
    }

    setStage('analyzing');
    setProgress('正在分析小说结构，提取写作规则...');
    setError(null);

    try {
      // 显示进度更新
      setTimeout(() => setProgress('正在提取情节结构...'), 2000);
      setTimeout(() => setProgress('正在提取人物设定...'), 5000);
      setTimeout(() => setProgress('正在提取节奏模式...'), 8000);
      setTimeout(() => setProgress('正在提取爽点设计...'), 11000);

      const result = await v3AnalyzeBook(targetBookId, { fastMode: true });
      setRulesInfo(result);
      setProgress('规则提取完成！');
      
      setTimeout(() => {
        setStage('rules');
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : '分析失败');
      setStage('upload');
    }
  };

  // 阶段 3: 生成大纲
  const handleGenerate = async () => {
    if (!rulesInfo) {
      setError('缺少规则集 ID');
      return;
    }

    setStage('generating');
    setProgress('正在基于规则生成大纲...');
    setError(null);

    try {
      setTimeout(() => setProgress('正在构建章节结构...'), 3000);
      setTimeout(() => setProgress('正在设计人物命运...'), 8000);
      setTimeout(() => setProgress('正在编写黄金三章...'), 15000);

      const result = await v3GenerateOutline({
        ruleSetId: rulesInfo.ruleSetId,
        userInput: {
          genre,
          desiredWords,
          style: style || undefined,
          customRequirements: customRequirements || undefined,
        },
      });

      setGeneratedResult(result);
      setProgress('大纲生成完成！');
      
      setTimeout(() => {
        setStage('result');
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : '生成失败');
      setStage('rules');
    }
  };

  // 重新开始
  const handleRestart = () => {
    setStage('upload');
    setBookInfo(null);
    setRulesInfo(null);
    setGeneratedResult(null);
    setSelectedFile(null);
    setError(null);
    setProgress('');
  };

  // 格式化数字
  const formatNumber = (num: number) => {
    if (num >= 10000) {
      return `${(num / 10000).toFixed(1)}万`;
    }
    return num.toLocaleString();
  };

  // 流派选项
  const genreOptions = [
    '都市', '玄幻', '仙侠', '历史', '军事', '科幻', '游戏', '竞技',
    '悬疑', '轻小说', '二次元', '现实主义', '短篇小说'
  ];

  // 字数选项
  const wordCountOptions = [
    { label: '30万字（短篇）', value: 300000 },
    { label: '50万字（中篇）', value: 500000 },
    { label: '80万字（中长篇）', value: 800000 },
    { label: '100万字（长篇）', value: 1000000 },
    { label: '150万字（超长篇）', value: 1500000 },
  ];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* 标题 */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-500" />
            智能拆书 V3
          </h1>
          <p className="text-slate-500">
            上传完整小说，AI 提取写作规则，基于规则生成大纲
          </p>
        </div>

        {/* 进度指示器 */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['upload', 'rules', 'result'].map((s, idx) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                stage === s 
                  ? 'bg-emerald-500 text-white'
                  : ['upload', 'rules', 'result'].indexOf(stage) > idx
                    ? 'bg-emerald-100 text-emerald-600'
                    : 'bg-slate-100 text-slate-400'
              }`}>
                {['upload', 'rules', 'result'].indexOf(stage) > idx ? (
                  <CheckCircle className="w-5 h-5" />
                ) : idx + 1}
              </div>
              {idx < 2 && (
                <div className={`w-12 h-0.5 mx-1 ${
                  ['upload', 'rules', 'result'].indexOf(stage) > idx
                    ? 'bg-emerald-500'
                    : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-4">
          <span>上传小说</span>
          <span>配置生成</span>
          <span>查看结果</span>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium mb-1">操作失败</p>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                关闭
              </button>
            </div>
          </div>
        )}

        {/* 阶段 1: 上传 */}
        <AnimatePresence mode="wait">
          {stage === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* 编码选择 */}
              <div className="flex items-center gap-4 bg-slate-50 rounded-xl p-3 border border-slate-100">
                <span className="text-sm text-slate-600">文件编码：</span>
                <div className="flex gap-2">
                  {(['auto', 'utf-8', 'gbk'] as const).map((enc) => (
                    <button
                      key={enc}
                      onClick={() => setEncoding(enc)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        encoding === enc
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                      }`}
                    >
                      {enc === 'auto' ? '自动检测' : enc.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* 拖拽区域 */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 ${
                  dragActive
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-300 hover:border-emerald-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />

                <div className="text-center">
                  {selectedFile ? (
                    <div className="space-y-2">
                      <FileText className="w-12 h-12 mx-auto text-emerald-500" />
                      <p className="text-slate-600 font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-slate-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                        className="text-sm text-red-500 hover:text-red-600"
                      >
                        重新选择
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center bg-slate-100">
                        <UploadIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-slate-600 mb-2">
                        拖拽 .txt 文件到此处，或点击上传
                      </p>
                      <p className="text-sm text-slate-400">
                        支持完整小说文本，建议 30 万字以上
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* 上传按钮 */}
              <button
                onClick={handleUpload}
                disabled={!selectedFile || stage !== 'upload'}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {stage !== 'upload' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    处理中...
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-5 h-5" />
                    上传并分析
                  </>
                )}
              </button>

              {/* 说明 */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm text-blue-700 font-medium mb-2">V3 版本说明</p>
                <ul className="text-sm text-blue-600 space-y-1">
                  <li>• 上传完整小说，AI 自动提取写作规则</li>
                  <li>• 基于提取的规则生成个性化大纲</li>
                  <li>• 生成大纲 + 黄金三章示范</li>
                  <li>• 使用 MiniMax M2.5 模型</li>
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 分析进度 */}
        {(stage === 'analyzing' || stage === 'generating') && (
          <div className="bg-emerald-50 rounded-xl p-8 border border-emerald-100 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-emerald-500 animate-spin" />
            <p className="text-lg font-medium text-emerald-700 mb-2">{progress}</p>
            <p className="text-sm text-emerald-600">请耐心等待，这可能需要几分钟...</p>
          </div>
        )}

        {/* 阶段 2: 规则配置 */}
        <AnimatePresence mode="wait">
          {stage === 'rules' && bookInfo && rulesInfo && (
            <motion.div
              key="rules"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* 书籍信息 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-500" />
                  已分析的小说
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400 mb-1">书名</p>
                    <p className="font-medium text-slate-700">{bookInfo.title}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">章节数</p>
                    <p className="font-medium text-slate-700">{bookInfo.metadata.totalChapters}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">总字数</p>
                    <p className="font-medium text-slate-700">{formatNumber(bookInfo.metadata.totalWords)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">编码</p>
                    <p className="font-medium text-slate-700">{bookInfo.metadata.encoding}</p>
                  </div>
                </div>
              </div>

              {/* 提取的规则 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-500" />
                  已提取的规则
                </h3>
                <div className="space-y-3">
                  {rulesInfo.rules && Object.entries(rulesInfo.rules).map(([key, value]: [string, any]) => (
                    <div key={key} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-700 text-sm">{key}</p>
                        <p className="text-xs text-slate-500 mt-1">{value?.summary || '已提取'}</p>
                      </div>
                    </div>
                  ))}
                  {!rulesInfo.rules && (
                    <p className="text-sm text-slate-500">规则提取完成</p>
                  )}
                </div>
              </div>

              {/* 生成配置 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-emerald-500" />
                  生成配置
                </h3>
                
                <div className="space-y-4">
                  {/* 流派选择 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      小说流派
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {genreOptions.map((g) => (
                        <button
                          key={g}
                          onClick={() => setGenre(g)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                            genre === g
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 字数选择 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      目标字数
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {wordCountOptions.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setDesiredWords(opt.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                            desiredWords === opt.value
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 风格要求 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      风格要求（可选）
                    </label>
                    <input
                      type="text"
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      placeholder="例如：轻松幽默、虐心、热血..."
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>

                  {/* 自定义需求 */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      自定义需求（可选）
                    </label>
                    <textarea
                      value={customRequirements}
                      onChange={(e) => setCustomRequirements(e.target.value)}
                      placeholder="描述你想要的特殊设定、剧情走向等..."
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-h-[100px] resize-y"
                    />
                  </div>
                </div>
              </div>

              {/* 生成按钮 */}
              <button
                onClick={handleGenerate}
                disabled={stage !== 'rules'}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(stage as Stage) === 'generating' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    生成中...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    生成大纲
                  </>
                )}
              </button>

              {/* 重新分析 */}
              <button
                onClick={() => handleAnalyze()}
                className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all cursor-pointer bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <RefreshCw className="w-4 h-4" />
                重新分析
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 阶段 3: 结果展示 */}
        <AnimatePresence mode="wait">
          {stage === 'result' && generatedResult && (
            <motion.div
              key="result"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* 大纲信息 */}
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {generatedResult.outline.title}
                    </h3>
                    <p className="text-sm text-emerald-600 mt-1">
                      {generatedResult.outline.genre} · {generatedResult.outline.chapters.length}章
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-600 text-sm rounded-full">
                    生成成功
                  </span>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-500 mb-2">故事简介</h4>
                  <p className="text-slate-700">{generatedResult.outline.synopsis}</p>
                </div>

                {/* 章节大纲 */}
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-3">章节大纲</h4>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {generatedResult.outline.chapters.map((chapter) => (
                      <div key={chapter.index} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium text-emerald-600">
                            第{chapter.index}章
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatNumber(chapter.wordCount)}字
                          </span>
                        </div>
                        <h5 className="font-medium text-slate-900 mb-1">{chapter.title}</h5>
                        <p className="text-sm text-slate-600 mb-2">{chapter.summary}</p>
                        {chapter.keyPoints && chapter.keyPoints.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {chapter.keyPoints.map((point, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-white border border-slate-200 rounded text-slate-500">
                                {point}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 黄金三章 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  黄金三章示范
                </h3>
                
                <div className="space-y-4">
                  {['chapter1', 'chapter2', 'chapter3'].map((chapter, idx) => (
                    <div key={chapter} className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-slate-900 mb-2">
                        第{idx + 1}章示范
                      </h4>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-[10]">
                        {generatedResult.goldenChapters[chapter as keyof typeof generatedResult.goldenChapters]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-4">
                <button
                  onClick={handleRestart}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  重新生成
                </button>
                <Link
                  to="/"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all cursor-pointer bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <CheckCircle className="w-5 h-5" />
                  完成
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
